import type { ChatMessage, PersistentState } from "../../types/PersistentState";
import { sendCustomError } from "../../lib/ApplicationPerormanceMonitoring";
import { guid } from "@otto-ec/global-resources/misc";
import { saveStateToStorage } from "./persistent-state-persistence";

/**
 *
 */
export type StateReducer = (oldState: any) => PersistentState;

/**
 *
 */
export const CURRENT_STATE_VERSION = "2";

/**
 *
 *
 */
const MIGRATIONS: Record<string, StateReducer> = {
    /*                                              */
    "1": (oldState: any): PersistentState => {
        return {
            version: "1",
            conversations: Array.isArray(oldState?.conversations)
                ? oldState.conversations
                : []
        };
    },

    /*                                                      */
    "2": (oldState: any): PersistentState => {
        return {
            ...oldState,
            version: "2",
            conversations: [
                {
                    id: guid(),
                    messages: [initialChatMessage()]
                }
            ]
        };
    }
};

/**
 *
 */
export function initialChatMessage(): ChatMessage {
    return {
        event_id: "initial",
        message: "Lass dich beraten & finde passende Produkte. Nutze unsere Fragen oder den Suchschlitz!",
        sender: "assistant",
        type: "initial.message",
        message_completed: true,
        timestamp: Date.now(),
    };
}

/**
 *
 *
 *
 *
 */
export function migrateState(oldState: any): PersistentState {
    try {
        /*                          */
        if (oldState === null || oldState === undefined) {
            throw new Error("Cannot migrate null or undefined state");
        }

        /*                                                             */
        const currentVersion = oldState?.version || "0";

        /*                                                       */
        if (currentVersion === CURRENT_STATE_VERSION) {
            return oldState as PersistentState;
        }

        /*                      */
        if (isNewerVersion(currentVersion, CURRENT_STATE_VERSION)) {
            throw new Error(`Cannot migrate from newer version ${currentVersion} to ${CURRENT_STATE_VERSION}`);
        }

        /*                                                  */
        let currentState = oldState;
        const versionPath = getVersionPath(currentVersion, CURRENT_STATE_VERSION);

        for (const targetVersion of versionPath) {
            const migrationReducer = MIGRATIONS[targetVersion];
            if (!migrationReducer) {
                throw new Error(`No migration found for version ${targetVersion}`);
            }

            /*                          */
            currentState = migrationReducer(currentState);

            if (!currentState) {
                throw new Error(`Migration reducer for version ${targetVersion} returned null/undefined`);
            }
        }

        /*                                  */
        saveStateToStorage(currentState);

        return currentState as PersistentState;

    } catch (error) {
        const errorMessage = (error as Error).message;

        sendCustomError("nucleus-cassandra-client-migration", "State migration failed", {
            fromVersion: oldState?.version || "unknown",
            toVersion: CURRENT_STATE_VERSION,
            error: errorMessage,
        });

        /*                                          */
        throw error;
    }
}

/**
 *
 *
 */
function getVersionPath(fromVersion: string, toVersion: string): string[] {
    const from = fromVersion === "0" ? 0 : parseInt(fromVersion, 10);
    const to = parseInt(toVersion, 10);

    if (isNaN(from) || isNaN(to) || from >= to) {
        return [];
    }

    const path: string[] = [];
    for (let i = from + 1; i <= to; i++) {
        path.push(i.toString());
    }

    return path;
}

/**
 *
 */
function isNewerVersion(versionA: string, versionB: string): boolean {
    const a = parseInt(versionA, 10);
    const b = parseInt(versionB, 10);

    if (isNaN(a) || isNaN(b)) {
        return false;
    }

    return a > b;
}

/**
 *
 */
export function validateMigratedState(state: any): state is PersistentState {
    return !!(
        state &&
        typeof state === 'object' &&
        typeof state.version === 'string' &&
        Array.isArray(state.conversations) &&
        state.conversations.every((conv: any) =>
            conv &&
            typeof conv === 'object' &&
            Array.isArray(conv.messages)
        )
    );
}
